import { getDB } from './index';

/**
 * Durable FIFO mutation queue. Every offline-affecting write goes through
 * here. Entries survive app restarts; the sync engine (Phase C) drains them
 * in order.
 *
 * Entry shape:
 *   { id, entity, op, recordId, babyId, payload, status, attempts,
 *     createdAt, lastAttemptAt, maybeSent, lastError }
 *   entity:   'event'
 *   op:       'create' | 'update' | 'delete'
 *   recordId: 'local-<uuid>' initially; rewritten on first create
 *   status:   'pending' | 'in_flight' | 'failed' | 'dead'
 *   lastAttemptAt: ISO stamp of the last dispatch; absent = never sent
 *   maybeSent:     true once a request went out whose outcome we never saw
 *
 * The local-ID problem (per shared-docs/offline-first-architecture.md):
 * when a create succeeds the server returns a real id. We must rewrite
 * the local row's PK AND any later queue entries that still reference the
 * old local id. `rewriteRecordId()` is the atomic helper.
 */

/**
 * How long an entry may sit in 'in_flight' before we assume the session that
 * dispatched it died mid-request. Comfortably above the 12 s request timeout
 * in services/sync/network.js, so an entry a live drain is actually working
 * on is never reclaimed out from under it.
 */
const STALE_IN_FLIGHT_MS = 30000;

/** An entry that provably never left the device (so it is safe to rewrite). */
function isUnsent(entry) {
  return Boolean(
    entry &&
      entry.status === 'pending' &&
      !entry.lastAttemptAt &&
      !entry.maybeSent &&
      (entry.attempts || 0) === 0
  );
}

export async function enqueue(entry) {
  const db = getDB();
  return db.mutationQueue.add({
    ...entry,
    status: 'pending',
    attempts: 0,
    createdAt: new Date().toISOString(),
  });
}

export async function listPending(limit = 100) {
  const db = getDB();
  // Entries stranded in 'in_flight' by a session that died mid-request (iOS
  // suspended the WKWebView on cellular, user force-quit) are never listed
  // again on their own, so the write would be lost forever. Reclaim them here
  // — this is the drain's only entry point, drainQueue() holds a module-level
  // guard so no second drain is running, and every op replays safely (creates
  // dedupe server-side on clientEventId). 'in_flight' is never listed
  // directly; it only re-enters the queue via this reset.
  await reclaimStaleInFlight();
  return db.mutationQueue
    .where('status')
    .anyOf('pending', 'failed')
    .limit(limit)
    .toArray();
}

/**
 * Reset in-flight entries whose dispatching session is gone back to 'pending'.
 * `maybeSent` records that the request may already have reached the server, so
 * callers never treat the entry as "nothing has been sent yet" (see
 * mergeIntoPendingCreate and router.routeDelete).
 */
export async function reclaimStaleInFlight(staleMs = STALE_IN_FLIGHT_MS) {
  const db = getDB();
  const cutoff = Date.now() - staleMs;
  let reclaimed = 0;
  await db.transaction('rw', db.mutationQueue, async () => {
    const inFlight = await db.mutationQueue
      .where('status')
      .equals('in_flight')
      .toArray();
    for (const entry of inFlight) {
      const stamp = Date.parse(entry.lastAttemptAt || '');
      // A live drain stamps lastAttemptAt as it dispatches, so anything older
      // than the cutoff — or from before that field existed — belongs to a
      // session that never came back.
      if (Number.isFinite(stamp) && stamp > cutoff) continue;
      await db.mutationQueue.update(entry.id, {
        status: 'pending',
        maybeSent: true,
      });
      reclaimed += 1;
    }
  });
  return reclaimed;
}

export async function markInFlight(id) {
  await getDB().mutationQueue.update(id, {
    status: 'in_flight',
    // Stamped so reclaimStaleInFlight() can tell a live attempt from a
    // stranded one, and so isUnsent() knows the entry has been dispatched.
    lastAttemptAt: new Date().toISOString(),
  });
}

/**
 * Fold an update into the row's create while that create is still queued and
 * has never been dispatched, so the pair replays as ONE correct POST.
 *
 * Replaying create-then-update instead would POST the create verbatim — an
 * offline sleep start still carries `endTime: null` — and the server treats
 * that as "fell asleep": the co-parent gets a 'Baby is asleep' push for a
 * sleep that ended hours ago, immediately followed by 'Sleep ended'.
 *
 * Only a provably unsent create is patched. Once a create has been dispatched
 * the server may already own the row, and a re-POST with the same
 * clientEventId returns the existing row untouched (eventTrackerHandler
 * createEvent) — folding into it there would silently drop the update.
 *
 * Returns true when the update was absorbed; the caller must then NOT enqueue
 * a separate op.
 */
export async function mergeIntoPendingCreate(recordId, patch) {
  if (!recordId) return false;
  const db = getDB();
  let createId = null;

  await db.transaction('rw', db.mutationQueue, async () => {
    const entries = await db.mutationQueue
      .where('recordId')
      .equals(recordId)
      .toArray();
    const create = entries.find((e) => e.op === 'create' && isUnsent(e));
    if (!create) return;

    const base = create.payload || {};
    const next = { ...base, ...(patch || {}) };
    // babyId + clientEventId are the create's own addressing and idempotency
    // keys — an update body must never overwrite them.
    if (base.babyId !== undefined) next.babyId = base.babyId;
    if (base.clientEventId !== undefined) next.clientEventId = base.clientEventId;
    if (base.data || patch?.data) {
      next.data = { ...(base.data || {}), ...(patch?.data || {}) };
    }

    await db.mutationQueue.update(create.id, { payload: next });
    createId = create.id;
  });

  if (createId == null) return false;

  // The drain snapshots an entry's payload before it marks it in flight, so a
  // create picked up in the instant between our read and our write would go
  // out with the pre-merge payload. Confirm the entry is still sitting
  // untouched; if it moved, report "not merged" so the caller queues the
  // update as its own op — a redundant PUT is harmless, a lost wake is not.
  const after = await db.mutationQueue.get(createId);
  return isUnsent(after);
}

export async function markFailed(id, err) {
  const db = getDB();
  const row = await db.mutationQueue.get(id);
  if (!row) return;
  const attempts = (row.attempts || 0) + 1;
  await db.mutationQueue.update(id, {
    status: attempts >= 8 ? 'dead' : 'failed',
    attempts,
    lastError: String(err?.message || err),
  });
}

export async function dropEntry(id) {
  await getDB().mutationQueue.delete(id);
}

/**
 * After a create succeeds, the new real id replaces the local-<uuid> on the
 * stored event AND on every subsequent queue entry that still references
 * the old id. Atomic so a crash mid-rewrite leaves a consistent DB.
 */
export async function rewriteRecordId(oldId, newId) {
  const db = getDB();
  await db.transaction('rw', db.events, db.mutationQueue, async () => {
    const followups = await db.mutationQueue
      .where('recordId')
      .equals(oldId)
      .toArray();
    // The create being processed is still in the queue at this point (push.js
    // drops it after the rewrite) — it is not a follow-up.
    const pendingFollowups = followups.filter((f) => f.op !== 'create');
    const event = await db.events.get(oldId);
    if (event) {
      await db.events.delete(oldId);
      // Only call the row synced when nothing else is queued for it. If an
      // update/delete is still waiting (e.g. a wake PUT queued behind its
      // create), the row must stay 'local' so a pull between this create and
      // that follow-up cannot clobber the local edit with the server's copy
      // (upsertFromServer only protects 'local' rows).
      await db.events.put({
        ...event,
        eventId: newId,
        syncStatus: pendingFollowups.length > 0 ? 'local' : 'synced',
      });
    }
    for (const f of followups) {
      await db.mutationQueue.update(f.id, { recordId: newId });
    }
  });
}

export async function pendingCount() {
  const db = getDB();
  return db.mutationQueue
    .where('status')
    .anyOf('pending', 'failed', 'in_flight')
    .count();
}
